import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import { useEffect, useState } from "react";

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatar?: string;
}

interface TestimonialsSectionProps {
  variant?: 'default' | 'compact';
  showTitle?: boolean;
}

const testimonials: Testimonial[] = [
  {
    id: "1",
    name: "Ravindu Perera",
    role: "Software Engineer",
    company: "TechFlow Solutions",
    content: "Found my dream Colombo IT job within 2 weeks! The AI matching feature is incredible - it connected me with opportunities I wouldn't have found elsewhere. This is definitely the best job board Sri Lanka has to offer.",
    rating: 5
  },
  {
    id: "2", 
    name: "Samantha Fernando",
    role: "Finance Executive",
    company: "Lanka Bank",
    content: "As a fresh graduate, I was struggling to find entry level finance jobs Sri Lanka. GreatJobs.lk's targeted matching helped me land my first role at a leading bank. The platform truly understands what employers are looking for.",
    rating: 5
  },
  {
    id: "3",
    name: "Kasun Silva", 
    role: "HR Manager",
    company: "Creative Digital",
    content: "We've tried many platforms, but GreatJobs.lk stands out as Sri Lanka's first AI job board that actually delivers quality candidates. The Colombo IT jobs we post here get the best responses from skilled developers.",
    rating: 5
  },
  {
    id: "4",
    name: "Nimali Jayasinghe",
    role: "Marketing Specialist", 
    company: "Growth Labs",
    content: "The job alert system is fantastic! I get notified about relevant opportunities in real-time. It's clear why this is considered the best job board Sri Lanka has - the user experience is unmatched.",
    rating: 5
  },
  {
    id: "5",
    name: "Chathura Mendis",
    role: "Data Analyst",
    company: "Analytics Pro",
    content: "After months of searching traditional job sites, I discovered GreatJobs.lk. Within days, I had interviews lined up for Colombo IT jobs that perfectly matched my skills. The AI technology really works!",
    rating: 5
  },
  {
    id: "6",
    name: "Priyanka Wickramasinghe", 
    role: "Financial Analyst",
    company: "Investment Partners",
    content: "Starting my career was challenging until I found this platform. The entry level finance jobs Sri Lanka section had exactly what I needed. The detailed job descriptions and company profiles made applying so much easier.",
    rating: 5
  }
];

export const TestimonialsSection = ({ variant = 'default', showTitle = true }: TestimonialsSectionProps) => {
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get translated testimonials
  const getTranslatedTestimonials = () => {
    return [
      {
        id: "1",
        name: t.testimonials.testimonial1.name,
        role: t.testimonials.testimonial1.role,
        company: t.testimonials.testimonial1.company,
        content: t.testimonials.testimonial1.content,
        rating: 5
      },
      {
        id: "2", 
        name: t.testimonials.testimonial2.name,
        role: t.testimonials.testimonial2.role,
        company: t.testimonials.testimonial2.company,
        content: t.testimonials.testimonial2.content,
        rating: 5
      },
      {
        id: "3",
        name: t.testimonials.testimonial3.name, 
        role: t.testimonials.testimonial3.role,
        company: t.testimonials.testimonial3.company,
        content: t.testimonials.testimonial3.content,
        rating: 5
      },
      {
        id: "4",
        name: t.testimonials.testimonial4.name,
        role: t.testimonials.testimonial4.role, 
        company: t.testimonials.testimonial4.company,
        content: t.testimonials.testimonial4.content,
        rating: 5
      },
      {
        id: "5",
        name: t.testimonials.testimonial5.name,
        role: t.testimonials.testimonial5.role,
        company: t.testimonials.testimonial5.company,
        content: t.testimonials.testimonial5.content,
        rating: 5
      },
      {
        id: "6",
        name: t.testimonials.testimonial6.name, 
        role: t.testimonials.testimonial6.role,
        company: t.testimonials.testimonial6.company,
        content: t.testimonials.testimonial6.content,
        rating: 5
      }
    ];
  };

  const translatedTestimonials = getTranslatedTestimonials();
  const displayTestimonials = variant === 'compact' ? translatedTestimonials.slice(0, 3) : translatedTestimonials;

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === displayTestimonials.length - 3 ? 0 : prevIndex + 1
      );
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [displayTestimonials.length]);

  return (
    <section className="py-16 px-4 bg-muted/20 overflow-hidden">
      <div className="container mx-auto max-w-6xl">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {t.testimonials.title}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t.testimonials.subtitle}
            </p>
          </div>
        )}
        
        {/* Carousel Container */}
        <div className="relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out gap-6"
              style={{ 
                transform: `translateX(-${currentIndex * (100 / 3)}%)`,
                width: `${(displayTestimonials.length * 100) / 3}%`
              }}
            >
              {displayTestimonials.map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="flex-shrink-0"
                  style={{ width: `${100 / displayTestimonials.length}%` }}
                >
                  <Card className="gradient-card shadow-card hover:shadow-primary transition-all duration-300 relative overflow-hidden h-full">
                    <CardContent className="p-6">
                      <div className="absolute top-4 right-4 opacity-10">
                        <Quote className="w-8 h-8 text-primary" />
                      </div>
                      
                      <div className="flex items-center mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      
                      <p className="text-muted-foreground mb-6 leading-relaxed line-clamp-3">
                        "{testimonial.content}"
                      </p>
                      
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-white font-semibold mr-3">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{testimonial.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {testimonial.role} at {testimonial.company}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
          
          {/* Carousel Indicators */}
          <div className="flex justify-center mt-8 gap-2">
            {Array.from({ length: displayTestimonials.length - 2 }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentIndex === index 
                    ? 'bg-primary w-8' 
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>
        </div>
        
      </div>
    </section>
  );
};